import noop from 'lodash/noop';
import {
	envType,
	originType,
	tenantType,
	userType,
} from '@atlassiansox/analytics-web-client/analytics-web-types';
import AnalyticsClient from '@atlassiansox/analytics-web-client/client';
import { CompressionRule } from '@atlassiansox/analytics-web-client/compression-rule';
import { buildCompressionFunction, canCompress } from '@atlassian/exposure-events-compressor/main';
import {
	UI_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	SCREEN_EVENT_TYPE,
	OPERATIONAL_EVENT_TYPE,
} from '@atlassian/jira-product-analytics-bridge';
import {
	type UnknownEvent,
	type AnalyticsWebClient,
	type OriginTracingHandlerMapping,
	type InitAttrs,
	type UIViewedAttributes,
	type AnalyticsEvent,
	isAnalyticsEvent,
} from '@jira/platform__observability-product-analytics-web-client-types';
import { getPerformance } from '@jira/ssr__browser-globals/src/getPerformance.tsx';
import { getWindow } from '@jira/ssr__browser-globals/src/getWindow.tsx';
import getPillarEnableHttps from '@jira/ssr__global-build-context/src/getPillarEnableHttps.tsx';
import getPillarLocalDevelopment from '@jira/ssr__global-build-context/src/getPillarLocalDevelopment.tsx';

/**
 * Serves as the primary interface for analytics operations within a web context. This class is responsible
 * for the initialization of the analytics framework, optimizing UI responsiveness by managing blocking durations,
 * and facilitating the capture and transmission of different analytics events. It ensures seamless integration
 * of user, product, and environmental attributes into analytics data, aiding in comprehensive data analysis and
 * insights generation. This class abstracts complex analytics operations, providing a simplified interface for
 * event tracking and analytics management.
 */
export default class DefaultAnalyticsWebClient implements AnalyticsWebClient {
	analyticsClient!: AnalyticsClient;

	// The time it took to initialise the client
	initTime!: number;

	// Number of times an event was triggered
	eventCount!: number;

	// Duration we're blocking the UI thread with operations
	blockingDuration!: number;

	updateAmplitudeSRClient?: Function;

	amplitudeSRAttributesInjector?: Function;

	resetBlockingDuration(): void {
		this.eventCount = 0;
		this.blockingDuration = 0;
	}

	init({
		tenantId,
		userId,
		subProduct,
		embeddedProduct,
		product,
		environment,
		locale,
		callback,
		shouldTriggerUIViewed = true,
		solutions,
		theme,
		orgId,
		workspaceId,
	}: InitAttrs): boolean {
		const performance = getPerformance();
		const now = () => performance?.now() ?? Date.now();
		const start = now();
		this.analyticsClient = new AnalyticsClient(
			{
				env: environment || envType.DEV,
				product,

				version: getWindow()?.BUILD_KEY || 'unknown',
				origin: originType.WEB,
				locale,
			},
			{
				useStargate: true,
				delayQueueCompressors: [new CompressionRule(canCompress, buildCompressionFunction(50))],
				...(getPillarLocalDevelopment()
					? // When running in the Pillar local dev loop, we need to ensure requests are over the right host
						{
							apiHostProtocol: getPillarEnableHttps() ? 'https' : 'http',
						}
					: {}),
				onNewSessionStarted: this.onNewSessionStarted,
			},
		);
		if (!this.analyticsClient) {
			return false;
		}
		if (tenantId) {
			this.analyticsClient.setTenantInfo(tenantType.CLOUD_ID, tenantId);
		}
		if (userId) {
			this.analyticsClient.setUserInfo(userType.ATLASSIAN_ACCOUNT, userId);
		}
		if (subProduct) {
			this.analyticsClient.setSubproduct(subProduct);
		}
		if (orgId) {
			this.analyticsClient.setOrgInfo(orgId);
		}
		if (workspaceId) {
			this.analyticsClient.setWorkspaceInfo(workspaceId);
		}
		this.clearUIViewedAttributes();
		if (solutions || theme) {
			const OStheme = getWindow()?.matchMedia?.('(prefers-color-scheme: dark)')?.matches
				? 'dark'
				: 'light';
			this.setUIViewedAttributes({
				...(solutions && {
					solutions,
				}),
				...(theme && {
					theme,
					OStheme,
				}),
			});
		}
		if (embeddedProduct) {
			this.analyticsClient.setEmbeddedProduct(embeddedProduct);
		}
		if (shouldTriggerUIViewed) {
			const callbackFn = typeof callback === 'function' ? callback : noop;
			this.analyticsClient.startUIViewedEvent(callbackFn);
		}

		// Set init time
		this.initTime = now() - start;
		return true;
	}

	sendEvent(event: AnalyticsEvent | UnknownEvent): void {
		if (isAnalyticsEvent(event)) {
			const { type, payload } = event;
			const performance = getPerformance();
			const now = () => performance?.now() ?? Date.now();
			const start = now();

			if (this.amplitudeSRAttributesInjector) {
				this.amplitudeSRAttributesInjector(payload);
			}

			switch (type) {
				case UI_EVENT_TYPE:
					this.analyticsClient.sendUIEvent(payload);
					break;
				case TRACK_EVENT_TYPE:
					this.analyticsClient.sendTrackEvent(payload);
					break;
				case OPERATIONAL_EVENT_TYPE:
					this.analyticsClient.sendOperationalEvent(payload);
					break;
				case SCREEN_EVENT_TYPE:
					this.analyticsClient.sendScreenEvent(payload);
					break;
				default:
					break;
			}

			// Increment the blocking duration
			this.blockingDuration += now() - start;
			this.eventCount += 1;
		}
	}

	isInitialized(): boolean {
		return this.analyticsClient && this.initTime > 0;
	}

	getInstance(): AnalyticsClient {
		return this.analyticsClient;
	}

	setSubProduct(subProduct: string): void {
		this.analyticsClient.setSubproduct(subProduct);
	}

	setUIViewedAttributes(attributes: UIViewedAttributes): void {
		this.analyticsClient.setUIViewedAttributes({
			...this.analyticsClient.getUIViewedAttributes(),
			...attributes,
		});
	}

	setEmbeddedProduct(embeddedProduct: string): void {
		this.analyticsClient.setEmbeddedProduct(embeddedProduct);
	}

	removeUIViewedAttribute(key: keyof UIViewedAttributes): void {
		const { [key]: value, ...rest } = this.analyticsClient.getUIViewedAttributes();
		this.analyticsClient.setUIViewedAttributes({
			...rest,
		});
	}

	clearUIViewedAttributes(): void {
		this.analyticsClient.clearUIViewedAttributes();
	}

	clearEmbeddedProduct(): void {
		this.analyticsClient.clearEmbeddedProduct();
	}

	stopApdexEvent(payload: unknown): void {
		this.analyticsClient.stopApdexEvent(payload);
	}

	setOriginTracingHandlers(originTracingHandlerMapping: OriginTracingHandlerMapping): void {
		this.analyticsClient.setOriginTracingHandlers(originTracingHandlerMapping);
	}

	onNewSessionStarted(): void {
		this.updateAmplitudeSRClient && this.updateAmplitudeSRClient();
	}

	setUpdateAmplitudeSRClient(callbackFn: Function): void {
		this.updateAmplitudeSRClient = callbackFn;
	}

	setAmplitudeSRAttributesInjector(callbackFn: Function): void {
		this.amplitudeSRAttributesInjector = callbackFn;
	}
}
